/**
 * PathTransitionSystem
 *
 * Handles smooth transitions between gameplay paths.
 * When triggered, animates both player position and camera to target path/T.
 * Player remains physical during transition (high mass) so they can push objects.
 */

import { Vector3 } from '@babylonjs/core';
import type { Character } from './Character';
import type { GameplayPath } from '../types/gameplay';
import { GameplayModeRegistry } from './movement/GameplayModeRegistry';
import { Easing } from '../core/Easing';
import { logger } from '../core/Logger';

/** Active transition state */
interface ActiveTransition {
  character: Character;
  sourcePath: GameplayPath;
  targetPath: GameplayPath;
  targetT: number;
  maxSpeed: number;
  initialDistance: number;
  startTime: number;
  originalMassMultiplier: number;
}

/** Transition configuration */
export interface PathTransitionConfig {
  /** Mass multiplier during transition (default: 10) */
  massMultiplier?: number;

  /** Distance threshold to consider arrived (default: 0.5m) */
  arrivalThreshold?: number;

  /** Easing function for camera blend (default: 'easeInOut') */
  cameraEasing?: keyof typeof Easing;
}

const DEFAULT_CONFIG: Required<PathTransitionConfig> = {
  massMultiplier: 10,
  arrivalThreshold: 0.5,
  cameraEasing: 'easeInOut'
};

class PathTransitionSystemImpl {
  private activeTransition: ActiveTransition | null = null;
  private config: Required<PathTransitionConfig> = { ...DEFAULT_CONFIG };
  private onTransitionComplete: (() => void) | null = null;

  /**
   * Configure the transition system
   */
  configure(config: PathTransitionConfig): void {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Start a transition to a target path and T value
   */
  startTransition(
    character: Character,
    sourcePath: GameplayPath,
    targetPath: GameplayPath,
    targetT: number,
    maxSpeed: number,
    onComplete?: () => void
  ): void {
    if (this.activeTransition) {
      logger.core.warn('PathTransitionSystem: Transition already in progress, aborting previous');
      this.cancelTransition();
    }

    // Calculate target position
    const targetPos = targetPath.constraint.getPointAt(targetT);
    const currentPos = character.getPosition();
    const initialDistance = Vector3.Distance(currentPos, targetPos);

    if (initialDistance < this.config.arrivalThreshold) {
      // Already at target, just switch mode
      logger.core.info('PathTransitionSystem: Already at target, switching mode immediately');
      this.applyModeSwitch(character, targetPath);
      onComplete?.();
      return;
    }

    // Store original mass state - TODO: Get from character when method exists
    // const originalMassMultiplier = character.getMassMultiplier();

    this.activeTransition = {
      character,
      sourcePath,
      targetPath,
      targetT,
      maxSpeed,
      initialDistance,
      startTime: performance.now(),
      originalMassMultiplier: 1 // TODO: Get from character when method exists
    };

    this.onTransitionComplete = onComplete ?? null;

    // Increase mass for physical transition
    // character.setMassMultiplier(this.config.massMultiplier);

    logger.core.info(
      `PathTransitionSystem: Starting transition to path "${targetPath.id}" at T=${targetT.toFixed(2)}, distance=${initialDistance.toFixed(1)}m`
    );
  }

  /**
   * Update the transition (call each frame)
   * @returns true if transition is active
   */
  update(deltaTime: number): boolean {
    if (!this.activeTransition) return false;

    const { character, targetPath, targetT, maxSpeed } = this.activeTransition;

    // Get target world position
    const targetPos = targetPath.constraint.getPointAt(targetT);
    const currentPos = character.getPosition();

    // Calculate direction and distance
    const toTarget = targetPos.subtract(currentPos);
    toTarget.y = 0; // Keep horizontal for now
    const distance = toTarget.length();

    // Check arrival
    if (distance < this.config.arrivalThreshold) {
      this.completeTransition();
      return false;
    }

    // Calculate velocity toward target
    const direction = toTarget.normalize();
    const speed = Math.min(maxSpeed, distance / deltaTime * 0.5); // Smooth approach

    // Apply velocity to character's desired movement
    const desiredVelocity = direction.scale(speed);
    character.setDesiredMovement(desiredVelocity);

    // Calculate progress for camera blend (0 to 1)
    const progress = 1 - distance / this.activeTransition.initialDistance;
    // TODO: Blend camera config based on progress
    // const easedProgress = Easing[this.config.cameraEasing](Math.max(0, Math.min(1, progress)));
    // cameraController.blendToPathConfig(targetPath.config.cameraConfigId, easedProgress);
    void progress; // Suppress unused warning for now

    return true;
  }

  /**
   * Cancel the current transition
   */
  cancelTransition(): void {
    if (!this.activeTransition) return;

    // Restore original mass - TODO: Implement when mass multiplier exists
    // const { character, originalMassMultiplier } = this.activeTransition;
    // character.setMassMultiplier(originalMassMultiplier);

    logger.core.info('PathTransitionSystem: Transition cancelled');

    this.activeTransition = null;
    this.onTransitionComplete = null;
  }

  /**
   * Check if a transition is in progress
   */
  isTransitioning(): boolean {
    return this.activeTransition !== null;
  }

  /**
   * Get current transition progress (0-1)
   */
  getProgress(): number {
    if (!this.activeTransition) return 0;

    const { targetPath, targetT, initialDistance } = this.activeTransition;
    const targetPos = targetPath.constraint.getPointAt(targetT);
    const distance = Vector3.Distance(this.activeTransition.character.getPosition(), targetPos);

    return 1 - distance / initialDistance;
  }

  private completeTransition(): void {
    if (!this.activeTransition) return;

    const { character, targetPath } = this.activeTransition;

    // Restore original mass - TODO: Implement when mass multiplier exists
    // character.setMassMultiplier(this.activeTransition.originalMassMultiplier);

    // Apply mode switch
    this.applyModeSwitch(character, targetPath);

    logger.core.info(
      `PathTransitionSystem: Transition complete, switched to path "${targetPath.id}" mode "${targetPath.mode}"`
    );

    const callback = this.onTransitionComplete;
    this.activeTransition = null;
    this.onTransitionComplete = null;

    callback?.();
  }

  private applyModeSwitch(_character: Character, path: GameplayPath): void {
    // Get the mode instance
    const mode = GameplayModeRegistry.get(path.mode);

    // Set active path and mode on character - TODO: Implement on Character
    // _character.setActivePath(path);
    // _character.setActiveMode(mode);

    logger.core.debug(
      `PathTransitionSystem: Applied mode "${mode.displayName}" from path "${path.id}"`
    );
  }
}

// Singleton instance
export const PathTransitionSystem = new PathTransitionSystemImpl();
